import { AppStatus, RequestState } from '@models/Common'
import { ConsentResponse, Consents } from '@models/Identity'
import { surveyStorage, SurveyStorageKeys } from '@plugins/storage'
import { Action } from '@utils/reducers/Action'
import environment from 'config/environment'
import { logReducerAction } from '../dev/devSessionLog'

export type ConsentState = RequestState &
  Omit<Consents, 'hasAcceptedCookies'> & {
    canProceed: boolean
    hasConsented: boolean
    hasConditions: boolean
    hasAutoFill: boolean
    hasReadConsent: boolean
    consentData?: ConsentResponse
  }

export const initialState: ConsentState = {
  status: AppStatus.IDLE,
  errorMessage: undefined,
  canProceed: false,
  hasConsented: false,
  hasConditions: false,
  hasAutoFill: false,
  hasReadConsent: false,
  isMajor: false,
  consentData: undefined,
}

export enum ConsentActionType {
  ToggleMajor = 'ToggleMajor',
  ToggleConditions = 'ToggleConditions',
  ToggleAutoFill = 'ToggleAutoFill',
  ConsentPending = 'ConsentPending',
  ConsentSuccess = 'ConsentSuccess',
  ConsentFailure = 'ConsentFailure',
  ToggleReadConsent = 'ToggleReadConsent',
}

const toggleConditions = (state: ConsentState): ConsentState => {
  const { hasConditions, hasReadConsent } = state
  const newHasConditions = !hasConditions
  const canProceed = newHasConditions && hasReadConsent
  return {
    ...state,
    canProceed: canProceed,
    hasConditions: newHasConditions,
  }
}

const toggleReadConsent = (state: ConsentState): ConsentState => {
  const { hasReadConsent, hasConditions } = state
  const newHasReadConsent = !hasReadConsent
  const canProceed = newHasReadConsent && hasConditions
  return {
    ...state,
    canProceed,
    hasReadConsent: newHasReadConsent,
  }
}

const toggleMajor = (state: ConsentState): ConsentState => {
  const { isMajor } = state
  return {
    ...state,
    isMajor: !isMajor,
  }
}

const toggleAutoFill = (state: ConsentState): ConsentState => {
  const { hasAutoFill } = state
  return {
    ...state,
    hasAutoFill: !hasAutoFill,
  }
}

const ConsentReducer = (
  state: ConsentState,
  action: Action<ConsentActionType, ConsentResponse | string | undefined>,
): ConsentState => {
  if (environment.isDev) {
    // eslint-disable-next-line no-console
    console.log({ reducer: 'consent', state, action })
    logReducerAction('consent', action)
  }

  switch (action.type) {
    case ConsentActionType.ConsentPending:
      return { ...state, status: AppStatus.PENDING }
    case ConsentActionType.ConsentFailure:
      return { ...state, status: AppStatus.ERROR, errorMessage: action.payload as string }
    case ConsentActionType.ConsentSuccess:
      void surveyStorage.setItem(SurveyStorageKeys.CONSENT_DATA, action.payload)
      return {
        ...state,
        status: AppStatus.IDLE,
        errorMessage: undefined,
        hasConsented: true,
        // Required checkboxes to submit — keep visible in progress tooltip after reload
        hasReadConsent: true,
        hasConditions: true,
        consentData: action.payload as ConsentResponse,
      }
    case ConsentActionType.ToggleConditions:
      return toggleConditions(state)

    case ConsentActionType.ToggleAutoFill:
      return toggleAutoFill(state)

    case ConsentActionType.ToggleMajor:
      return toggleMajor(state)

    case ConsentActionType.ToggleReadConsent:
      return toggleReadConsent(state)

    default:
      return state
  }
}

export default ConsentReducer
